import { useQuery } from '@tanstack/react-query';
import { Building2, Check, ArrowLeftRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { switchChurchAndOpen } from '@/lib/switch-church-workspace';
import { api } from '@/lib/api';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import {
  campusInViewLabel,
  churchOrgName,
  resolveHeadOffice,
  shouldShowCampusInTopBar,
} from '@/utils/campus-display';
import { cn } from '@/utils/cn';

interface BranchOption {
  id: string;
  campusName: string;
  isHead: boolean;
  status: string;
}

export const BranchSwitcher = () => {
  const { t } = useTranslation('branches');
  const navigate = useNavigate();
  const church = useChurchStore((state) => state.church);
  const headOffice = useChurchStore((state) => state.headOffice);
  const memberships = useChurchStore((state) => state.memberships);
  const membership = useChurchStore((state) => state.membership);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const allowsFeature = useChurchStore((state) => state.allowsFeature);
  const switchToBranch = useChurchStore((state) => state.switchToBranch);

  const hasMultiBranch =
    Boolean(church) && isModuleEnabled('branches') && allowsFeature('multi_branch');
  const canSwitch = hasPermission(membership, PERMISSIONS.BRANCHES_SWITCH);

  const branches = useQuery({
    queryKey: ['branches', 'switcher', church?.id],
    queryFn: () => api.get<{ data: BranchOption[] }>('/branches'),
    enabled: Boolean(church && canSwitch && hasMultiBranch),
  });

  if (!church) {
    return null;
  }

  const options = (branches.data?.data ?? []).filter((row) => row.status === 'active');
  const showSwitchControl = Boolean(canSwitch && hasMultiBranch && options.length > 1);
  const resolvedHeadOffice = resolveHeadOffice(church, headOffice, memberships);
  const orgName = churchOrgName(church, resolvedHeadOffice);
  const campusLabel = shouldShowCampusInTopBar(church, memberships)
    ? campusInViewLabel(church, t('switcher.headOffice'))
    : null;

  return (
    <div className="flex min-w-0 items-center gap-3">
      {church.logoUrl ? (
        <img
          src={church.logoUrl}
          alt=""
          className="size-8 shrink-0 rounded-md object-cover"
        />
      ) : (
        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-sunken text-fg-tertiary">
          <Building2 className="size-4" aria-hidden />
        </span>
      )}

      <div className="flex min-w-0 items-center gap-2">
        <p className="min-w-0 truncate text-body font-medium">
          {orgName}
          {campusLabel ? (
            <span className="font-normal text-fg-secondary"> · {campusLabel}</span>
          ) : null}
        </p>
        {showSwitchControl ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="md"
                className="h-8 shrink-0 px-2 text-brand hover:bg-surface-sunken sm:h-10 sm:hover:bg-transparent sm:hover:underline"
                aria-label={t('switcher.switch')}
              >
                <ArrowLeftRight className="size-4 sm:hidden" aria-hidden />
                <span className="hidden sm:inline">{t('switcher.switch')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-56">
              <DropdownMenuLabel>{t('switcher.label')}</DropdownMenuLabel>
              {options.map((row) => {
                const selected = row.id === church.id;
                return (
                  <DropdownMenuItem
                    key={row.id}
                    disabled={selected}
                    onSelect={() => {
                      if (selected) return;
                      void switchChurchAndOpen({
                        action: () => switchToBranch(row.id),
                        navigate,
                        churchName: row.isHead
                          ? `${row.campusName} (${t('switcher.headOffice')})`
                          : row.campusName,
                      });
                    }}
                  >
                    <Check
                      className={cn('size-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {row.isHead
                        ? `${row.campusName} (${t('switcher.headOffice')})`
                        : row.campusName}
                    </span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
};
